import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import AboutBigButton from "@/components/AboutBigButton";
import LanguageToggle from "@/components/LanguageToggle";
import { Eye, EyeSlash, ArrowLeft, ArrowRight, Car as CarIcon, Info, User as UserIcon } from "@phosphor-icons/react";
import PhoneInput from "@/components/PhoneInput";
import { toEnglishDigits } from "@/utils/toEnglishDigits";

export default function Login() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const { user, login, driverLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [driverName, setDriverName] = useState("");
  const [phone, setPhone] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submitEmail = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success(t("auth.toastWelcome"));
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDriver = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await driverLogin(phone.trim(), accessCode.trim());
      toast.success(t("auth.toastDriverWelcome"));
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Directional helpers — reads more naturally when RTL vs LTR.
  const align = isEn ? "text-left" : "text-right";
  const padEye = isEn ? "pr-12" : "pl-12";
  const eyeSide = isEn ? "right-3" : "left-3";
  const iconSide = isEn ? "left-3" : "right-3";
  const padIcon = isEn ? "pl-9" : "pr-9";

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      dir={isEn ? "ltr" : "rtl"}
      style={{
        background:
          "radial-gradient(ellipse at top left, rgba(201,169,97,0.10) 0%, transparent 45%), radial-gradient(ellipse at bottom right, rgba(201,169,97,0.06) 0%, transparent 45%), #050a15",
      }}
    >
      <div className="w-full max-w-md relative">
        {/* Language toggle — top-left when RTL, top-right when LTR */}
        <div className={"absolute top-0 " + (isEn ? "right-0" : "left-0")}>
          <LanguageToggle />
        </div>

        {/* Brand */}
        <div className="flex items-center justify-center mb-6 pt-8" data-testid="brand-logo">
          <Logo variant="full" size={200} className="logo-hero" />
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-white text-center mt-2">
          {t("auth.welcomeBack")}
        </h1>
        <p className="text-white/50 text-center mt-3 text-sm">
          {mode === "email" ? t("auth.companySubtitle") : t("auth.driverSubtitle")}
        </p>

        {mode === "email" ? (
          <form onSubmit={submitEmail} className="mt-10 space-y-4">
            <div>
              <label className={"block text-sm text-white/70 mb-2 " + align}>{t("auth.email")}</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@company.com"
                data-testid="login-email"
                className={"mz-input " + align}
              />
            </div>
            <div>
              <label className={"block text-sm text-white/70 mb-2 " + align}>{t("auth.password")}</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password"
                  className={"mz-input " + align + " " + padEye}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className={"absolute top-1/2 -translate-y-1/2 text-primary hover:text-[#E1C484] transition-colors " + eyeSide}
                  aria-label={t("auth.passwordShow")}
                  data-testid="toggle-password"
                >
                  {showPw ? <EyeSlash size={22} /> : <Eye size={22} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full mt-2 bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold py-4 rounded-xl transition-all disabled:opacity-60"
            >
              {submitting ? t("auth.loggingIn") : t("auth.loginBtn")}
            </button>
          </form>
        ) : (
          <form onSubmit={submitDriver} className="mt-10 space-y-4">
            <div>
              <label className={"block text-sm text-white/70 mb-2 " + align}>{t("auth.driverName")}</label>
              <div className="relative">
                <UserIcon size={16} className={"absolute top-1/2 -translate-y-1/2 text-primary/60 " + iconSide} />
                <input
                  type="text"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                  placeholder={t("auth.driverNamePh")}
                  data-testid="driver-name-input"
                  className={"mz-input " + align + " " + padIcon}
                />
              </div>
            </div>
            <div>
              <label className={"block text-sm text-white/70 mb-2 " + align}>{t("auth.phone")}</label>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                required
                testId="driver-phone"
                placeholder="5XXXXXXXX"
              />
            </div>
            <div>
              <label className={"block text-sm text-white/70 mb-2 " + align}>{t("auth.accessCode")}</label>
              <input
                type="text"
                required
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength="6"
                value={accessCode}
                onChange={(e) => setAccessCode(toEnglishDigits(e.target.value).replace(/\D/g, ""))}
                placeholder="●●●●●●"
                data-testid="driver-code"
                className="mz-input text-center tracking-[0.5em] text-xl num"
                dir="ltr"
              />
            </div>
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-[12px] text-white/75 flex items-start gap-2">
              <Info size={16} weight="fill" className="text-primary shrink-0 mt-0.5" />
              <span>{t("auth.driverInfo")}</span>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="driver-login-submit"
              className="w-full mt-2 bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold py-4 rounded-xl transition-all disabled:opacity-60"
            >
              {submitting ? t("auth.loggingIn") : t("auth.driverLoginBtn")}
            </button>
            <Link
              to="/driver-register"
              data-testid="driver-register-link"
              className="block text-center py-3 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 text-sm font-bold"
            >
              {t("auth.driverRegisterCta")}
            </Link>
          </form>
        )}

        <div className="mt-6 flex flex-col items-center gap-3 text-sm">
          {mode === "email" ? (
            <button
              onClick={() => setMode("driver")}
              className="text-primary hover:text-[#E1C484] transition-all inline-flex items-center gap-2"
              data-testid="switch-driver-mode"
            >
              <CarIcon size={18} weight="duotone" />
              {t("auth.switchToDriver")}
            </button>
          ) : (
            <button
              onClick={() => setMode("email")}
              className="text-primary hover:text-[#E1C484] transition-all inline-flex items-center gap-2"
              data-testid="switch-email-mode"
            >
              {t("auth.switchToCompany")}
              {isEn ? <ArrowRight size={14} /> : <ArrowLeft size={14} />}
            </button>
          )}
          <div className="text-white/50">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="text-primary hover:text-[#E1C484]" data-testid="register-link">
              {t("auth.registerNow")}
            </Link>
          </div>
        </div>
        <AboutBigButton />
      </div>
    </div>
  );
}
